import { AbstractProgram, AbstractSession, AbstractUser, StripeProduct } from "@kvix/shared";
import { Button, SelectableDate, Text } from "@kvix/ui";
import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import TimeIcon from "@material-ui/icons/AccessTime";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import i18next, { TFunction } from "i18next";
import React, { FC, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { KvixUserContext, Sessions } from "../../contexts/user";
import { Guard } from "./Guard";

interface Props {
  program: AbstractProgram;
  sessions: AbstractSession[];
  onSubmit?: (sessions: AbstractSession[]) => Promise<void>;
  onCancel?: (sessions: AbstractSession[]) => Promise<void>;
  requiredProduct: StripeProduct;
}

const useBookedSessionIds = (sessionsState: Sessions) => {
  const sessions = sessionsState.booked || [];

  return sessions.map((session) => session.id);
};

export const SessionBooker: FC<Props> = (props) => {
  const [selectedSessions, setSelectedSessions] = useState<AbstractSession[]>(
    []
  );
  const { t, ready } = useTranslation([
    "program",
    "general",
    "premiumPromptLive",
  ]);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const [showConfirmBooking, setShowConfirmBooking] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [cancelingSession, setCancelingSession] =
    useState<AbstractSession>(null);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  const { user, sessions } = useContext(KvixUserContext);
  const bookedSessionIds = useBookedSessionIds(sessions);
  const isProgramInstructor = user?.id === props.program.instructorId;

  const toggleSession = (session: AbstractSession) => {
    if (selectedSessions.includes(session)) {
      setSelectedSessions(
        selectedSessions.filter(
          (selectedSession) => selectedSession !== session
        )
      );
    } else {
      setSelectedSessions([...selectedSessions, session]);
    }
  };

  const submit = async () => {
    if (props.onSubmit) {
      setLoading(true);
      try {
        await props.onSubmit(selectedSessions);
        setLoading(false);
        setShowConfirmBooking(true);
      } catch (error) {
        setLoading(false);
        setErrorMessage(error.message);
        setErrorModalOpen(true);
      }
    }
  };

  const closeConfirmation = () => {
    setShowConfirmBooking(false);
    setSelectedSessions([]);
  };

  const cancelBookedSession = async (session: AbstractSession) => {
    setLoadingCancel(true);

    try {
      await props.onCancel([session]);
      setCancelingSession(null);
      setLoadingCancel(false);
    } catch (error) {
      setCancelingSession(null);
      setLoadingCancel(false);

      setErrorMessage(error.message);
      setErrorModalOpen(true);
    }
  };

  if (!ready) {
    return null;
  }

  return (
    <Grid container spacing={3} justify="flex-start">
      <Grid item xs={12}>
        <Text variant="h4" gutterBottom>
          {t("availableSessionTimes")}
        </Text>

        {isProgramInstructor ? (
          <Text color="error">{t("availableSessionTimesErrorText")}</Text>
        ) : (
          <Text>{t("availableSessionTimesText")}</Text>
        )}
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={3}>
          {props.sessions.map((session) => {
            const isBooked = bookedSessionIds.includes(session.id);
            const isSelected = selectedSessions.includes(session);

            return (
              <Grid item xs={6} sm={4} key={session.id}>
                <Guard.Click
                  exception={user && !session.requiredProductAnytime}
                  requiredProduct={props.requiredProduct}
                >
                  <SelectableDate
                    date={session.start}
                    selected={isBooked || isSelected}
                    cancelable={isBooked}
                    disabled={isProgramInstructor}
                    cancelTooltip={t("cancelBookingTooltip")}
                    onClick={() => toggleSession(session)}
                    onCancelClick={() => setCancelingSession(session)}
                  />
                </Guard.Click>
              </Grid>
            );
          })}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          loading={loading}
          onClick={submit}
          disabled={selectedSessions.length === 0}
        >
          {t("general:buttons.bookClasses")}
        </Button>
        <BookingCompletedModal
          program={props.program}
          sessions={selectedSessions}
          open={showConfirmBooking}
          user={user}
          fullScreen={fullScreen}
          t={t}
          onClose={closeConfirmation}
        />

        <CancelBookingModal
          title={t("bookClassModal.cancelBookingTitle")}
          message={t("bookClassModal.cancelBookingMessage")}
          program={props.program}
          session={cancelingSession}
          fullScreen={fullScreen}
          loading={loadingCancel}
          open={!!cancelingSession}
          t={t}
          onConfirm={() => cancelBookedSession(cancelingSession)}
          onClose={() => setCancelingSession(null)}
        />

        <ErrorModal
          open={errorModalOpen}
          message={errorMessage}
          title="Failed to book session(s)"
          t={t}
          onClose={() => setErrorModalOpen(false)}
        />
      </Grid>
    </Grid>
  );
};

const Modal = styled(Paper)`
  &.MuiDialog-paper {
    overflow: visible;
    &:before,
    &:after {
      content: "";
      position: absolute;

      width: 100%;
      height: 8px;

      background-color: transparent;
      background-image: radial-gradient(transparent 50%, white 55%);
      background-size: 13px 24px;
    }

    &:before {
      top: 0;
      background-position: 0px 8px;
      transform: translateY(-100%);
    }
    &:after {
      bottom: 0;
      background-position: 0px 24px;
      transform: translateY(100%);
    }
  }
`;

const BookingCompletedModal: React.FC<{
  sessions: AbstractSession[];
  program: AbstractProgram;
  user: AbstractUser;
  fullScreen: boolean;
  open: boolean;
  t: TFunction;
  onClose: () => void;
}> = (props) => {
  const currentLanguage = i18next.language;
  const getSessionDate = (date: Date) => {
    return format(new Date(date), "iiii dd MMM HH:mm", {
      locale: currentLanguage === "sv-SE" && sv,
    });
  };

  const capitalize = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  return (
    <div>
      <Dialog
        open={props.open}
        onClose={props.onClose}
        fullScreen={props.fullScreen}
        PaperComponent={Modal}
        style={{ background: "transparent" }}
        scroll="paper"
      >
        <div
          style={{
            margin: "auto",
            overflow: "auto",
            padding: props.fullScreen ? 0 : 24,
            background: "#FFF",
          }}
        >
          <DialogContent style={{ width: 320 }}>
            <Box mb={3}>
              <Avatar
                src={props.program.imageUrl}
                style={{ width: 120, height: 120 }}
              />
            </Box>

            <Text variant="h4" color="textPrimary" gutterBottom>
              {props.t("bookClassModal.successfulBookingTitle")}
            </Text>

            <Text variant="h4" color="primary" gutterBottom>
              {props.program.title}
            </Text>

            <Box mt={2}>
              <Text>
                {props.t("bookClassModal.successfulBookingText", {
                  numberOfBookedSessions: props.sessions.length,
                })}
              </Text>
            </Box>

            <Box margin="0 -24px 16px 0">
              {props.sessions.map((session) => (
                <Text style={{ fontWeight: 500 }} key={session.id}>
                  {capitalize(getSessionDate(session.start))}
                </Text>
              ))}
            </Box>

            <Box>
              <Text>
                {props.user?.email
                  ? props.t("bookClassModal.successfulBookingEmailText")
                  : props.t("bookClassModal.noEmailBookingEmailText")}

                <span
                  style={{
                    width: "100%",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: 500,
                  }}
                >{` ${props.user?.email}`}</span>
              </Text>
            </Box>
            <Box mt={1}>
              <Text>
                <Link to="/me/account">
                  {props.t("bookClassModal.successfulBookingChangeEmail")}
                </Link>
              </Text>
            </Box>
          </DialogContent>

          <DialogActions style={{ justifyContent: "start" }}>
            <Text
              onClick={props.onClose}
              color="primary"
              style={{ cursor: "pointer" }}
            >
              {props.t("general:buttons.close")}
            </Text>
          </DialogActions>
        </div>
      </Dialog>
    </div>
  );
};

const TimeRow = styled.div`
  display: flex;
  align-items: center;

  margin: 6px 0;

  svg {
    margin-right: 6px;
    font-size: 20px;
    transform: translateY(-0.04em);
  }
`;

const CancelBookingModal: React.FC<{
  open: boolean;
  title: string;
  message: string;
  loading: boolean;
  program: AbstractProgram;
  session: AbstractSession;
  fullScreen: boolean;
  t: TFunction;
  onClose: () => void;
  onConfirm: () => void;
}> = (props) =>
  props.session && (
    <Dialog
      open={props.open}
      onBackdropClick={props.onClose}
      fullScreen={props.fullScreen}
    >
      <div style={{ margin: "auto", padding: props.fullScreen ? 0 : 12 }}>
        <DialogContent style={{ width: 320 }}>
          <Box mb={3}>
            <Avatar
              src={props.program.imageUrl}
              style={{ width: 120, height: 120 }}
            />
          </Box>

          <Text variant="h4">{props.program.title}</Text>

          <Text variant="h6" color="primary" gutterBottom>
            <TimeRow>
              <TimeIcon color="primary" />
              {format(new Date(props.session.start), "iiii HH:mm", {
                locale: i18next.language === "sv-SE" && sv,
              })}
            </TimeRow>
          </Text>

          <Text color="textPrimary" gutterBottom>
            {props.t("bookClassModal.cancelBookingText")}
          </Text>
        </DialogContent>

        <DialogActions>
          <Button color="primary" variant="text" onClick={props.onClose}>
            {props.t("general:buttons.goBack")}
          </Button>

          <Button
            color="primary"
            onClick={props.onConfirm}
            loading={props.loading}
          >
            {props.t("general:buttons.yesCancel")}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );

const ErrorModal: React.FC<{
  open: boolean;
  title: string;
  message: string;
  t: TFunction;
  onClose: () => void;
}> = (props) => (
  <Dialog open={props.open} onBackdropClick={props.onClose}>
    <DialogTitle>{props.title}</DialogTitle>
    <DialogContent>
      <Text variant="body1" gutterBottom>
        {props.message}
      </Text>
    </DialogContent>
    <DialogActions>
      <Button color="primary" onClick={props.onClose}>
        {props.t("general:buttons.ok")}
      </Button>
    </DialogActions>
  </Dialog>
);
